import type { AdminUser } from '@/api';
import { initials } from '@/pages/dashboard/format';

export default function UsersTable({
  users,
  onSelect,
  onToggleBan,
  onImpersonate,
  onOverrideCredits,
}: {
  users: AdminUser[];
  onSelect: (u: AdminUser) => void;
  onToggleBan: (u: AdminUser) => void;
  onImpersonate: (u: AdminUser) => void;
  onOverrideCredits: (u: AdminUser) => void;
}) {
  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-texts border-b border-border">
              <th className="font-medium px-4 py-3">User</th>
              <th className="font-medium px-4 py-3">Role</th>
              <th className="font-medium px-4 py-3">Credits</th>
              <th className="font-medium px-4 py-3">Tokens</th>
              <th className="font-medium px-4 py-3">Projects</th>
              <th className="font-medium px-4 py-3">Status</th>
              <th className="font-medium px-4 py-3">Last active</th>
              <th className="font-medium px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-b border-border last:border-b-0 hover:bg-surface2 transition-colors"
              >
                <td className="px-4 py-3">
                  <button onClick={() => onSelect(u)} className="flex items-center gap-3 text-left">
                    <span className="w-8 h-8 shrink-0 rounded-full bg-accentSoft border border-accentLine flex items-center justify-center text-xs font-medium text-accent">
                      {initials(u.name || u.email)}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-textp font-medium truncate">{u.name}</span>
                      <span className="block text-xs text-texts truncate">{u.email}</span>
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${
                      u.role === 'admin'
                        ? 'bg-accentSoft text-accent'
                        : 'bg-surface2 text-texts border border-border'
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-textp">{u.credits}</td>
                <td className="px-4 py-3 font-mono text-textp">{u.tokens.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-textp">{u.projects.length}</td>
                <td className="px-4 py-3">
                  <span
                    className={`whitespace-nowrap inline-flex items-center gap-1.5 text-xs ${
                      u.banned ? 'text-error' : 'text-success'
                    }`}
                  >
                    <i className={`${u.banned ? 'ri-forbid-line' : 'ri-checkbox-circle-line'}`} />
                    {u.banned ? 'Banned' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3 text-texts">{u.last_active}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => onImpersonate(u)}
                      disabled={u.banned || u.role === 'admin'}
                      title="Log in as"
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-texts hover:text-textp hover:bg-surface2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="ri-login-box-line" />
                    </button>
                    <button
                      onClick={() => onOverrideCredits(u)}
                      title="Override credits"
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-texts hover:text-textp hover:bg-surface2 transition-colors"
                    >
                      <i className="ri-flashlight-line" />
                    </button>
                    <button
                      onClick={() => onToggleBan(u)}
                      title={u.banned ? 'Unban' : 'Ban'}
                      className={`whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                        u.banned ? 'text-success hover:bg-surface2' : 'text-texts hover:text-error hover:bg-surface2'
                      }`}
                    >
                      <i className={u.banned ? 'ri-check-line' : 'ri-forbid-line'} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
